import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { getLocations, getCurrentLocationCoordinates } from '../../../../shared/repos/graphql/locations';
import { getStores } from '../../../../shared/repos/graphql/store';
import { getCurrentPosition } from '../../../../shared/utilities/geolocation';
import { orderTypeIds } from '../../../../shared/constants/order';
import ddLog from '../../../../shared/utilities/datadog/log';

const MapPinIcon = require("./imgs/MapPin.png");


const AddressAutocomplete = ({
  value,
  onChange,
  onStoreSelect,
  onStoresLoaded,
  onAddressSelect,
  placeholder,
  className,
  disabled,
  delivery,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [isSelectingAddress, setIsSelectingAddress] = useState(false);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Get current location once when component mounts
  useEffect(() => {
    const getCurrentLocation = async () => {
      return new Promise((resolve) => {
        getCurrentPosition((response) => {
          if (response && response.position) {
            const { latitude, longitude } = response.position.coords;
            resolve({ lat: latitude, lng: longitude });
          } else {
            // Use server location as fallback (same as existing geolocation utilities)
            getCurrentLocationCoordinates().then((result) => {
              const { lat, lng } = result.data.currentLocationCoordinates;
              resolve({ lat, lng });
            }).catch(() => {
              // This should rarely happen as getCurrentLocationCoordinates handles fallbacks
              resolve({ lat: 0, lng: 0 });
            });
          }
        });
      });
    };

    getCurrentLocation().then((location) => {
      setCurrentLocation(location);
    });
  }, []);

  // Search function (not debounced)
  const searchAddresses = useCallback(async (input) => {
    if (input.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Don't search if we don't have current location yet
    if (!currentLocation) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Use stored current location for better search results
      const response = await getLocations(
        input,
        currentLocation.lat,
        currentLocation.lng,
        false, // fullyQualified
        true,  // showDorms
        null   // filter
      );

      const locations = response.data?.locationSearch || [];

      // Transform locations to match our expected format
      const addressSuggestions = locations.map(location => ({
        id: location.id,
        formattedAddress: location.address1,
        address: location.address1,
        city: location.addressDetails?.city || '',
        state: location.addressDetails?.state || '',
        zip: location.addressDetails?.postcode || '',
        country: location.addressDetails?.country || '',
        lat: location.lat,
        lng: location.lng,
        placeId: location.externalId,
        store: location.store
      }));

      setSuggestions(addressSuggestions);
      setShowSuggestions(addressSuggestions.length > 0);
      setSelectedIndex(-1);
    } catch (err) {
      ddLog.logError(err);
      setError('Failed to load address suggestions');
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, [currentLocation]);

  // Create debounced function once
  const debouncedSearch = useCallback(
    _.debounce(searchAddresses, 300),
    [searchAddresses]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const inputValue = e.target.value;

    // Don't call onChange if we're in the middle of selecting an address
    if (!isSelectingAddress) {
      onChange(inputValue);
    }

    // Use the debounced search function
    debouncedSearch(inputValue);
  };

  // Handle address selection
  const handleAddressSelect = async (address) => {
    setIsSelectingAddress(true);
    setShowSuggestions(false);
    onChange(address.formattedAddress);
    setSelectedIndex(-1);

    // Pass address coordinates to parent component
    if (onAddressSelect) {
      onAddressSelect({
        address: address.formattedAddress,
        lat: address.lat,
        lng: address.lng,
        placeId: address.placeId
      });
    }

    try {
      setIsLoading(true);

      // Search for stores near the selected address
      const response = await getStores({
        lat: address.lat,
        lng: address.lng,
        externalId: address.placeId,
        customAddress: address.formattedAddress,
        orderTypeId: delivery ? orderTypeIds.delivery : orderTypeIds.pickup
      });

      const stores = response.data?.storeSearch?.stores || [];

      // Pass stores to parent component
      if (onStoresLoaded) {
        onStoresLoaded(stores);
      }

      // If there's a store and onStoreSelect callback, select the closest one
      if (stores.length > 0 && onStoreSelect) {
        const closestStore = stores[0];
        onStoreSelect(closestStore);
      }
    } catch (err) {
      ddLog.logError(err);
      setError('Failed to load store information');
    } finally {
      setIsLoading(false);
      setIsSelectingAddress(false);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleAddressSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



  return (
    <div className="relative">
      {/* Input Field */}
      <div className="border-[1px] border-[#5A3770] bg-[#2C0C37] rounded-[40px] flex gap-2 items-center justify-center px-2 min-h-[52px] pl-4">
        <img
          src={MapPinIcon}
          alt="address"
          aria-hidden="true"
          className="w-4 h-5 object-contain"
        />
        <input
          ref={inputRef}
          type="text"
          className={`text-[#B4A5BB] w-full bg-transparent outline-[0] h-11 text-base font-medium font-montserratMedium selection:text-white selection:bg-[#5A3770] autofill:!text-[#B4A5BB] autofill:!bg-transparent ${className}`}
          style={{
            WebkitTextFillColor: '#B4A5BB',
            WebkitBoxShadow: '0 0 0 1000px transparent inset',
            transition: 'none',
            WebkitTransition: 'none'
          }}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(suggestions.length > 0)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        {isLoading && (
          <div className="w-4 h-4 border-2 border-[#B4A5BB] border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-2 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-[#2C0C37] border border-[#5A3770] rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion?.id}
              role="button"
              tabIndex={0}
              className={`px-4 py-3 cursor-pointer text-[#B4A5BB] hover:bg-[#5A3770] hover:text-white transition-colors ${
                index === selectedIndex ? 'bg-[#5A3770] text-white' : ''
              }`}
              onClick={() => handleAddressSelect(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleAddressSelect(suggestion);
                  e.preventDefault(); // prevent scrolling for Space
                }
              }}
            >
              <div className="font-medium">{suggestion.formattedAddress}</div>
              {delivery ? (
                // For delivery: show address details
                suggestion.city && suggestion.state && (
                  <div className="text-sm opacity-75">
                    {`${suggestion.city}, ${suggestion.state} ${suggestion.state}`}
                  </div>
                )
              ) : (
                // For pickup: show store details
                <div className="text-sm opacity-75">
                  {suggestion.address}
                  {suggestion.city && suggestion.state && (
                    <span>
                      {`• ${suggestion.city}, ${suggestion.state}`}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

AddressAutocomplete.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onStoreSelect: PropTypes.func,
  onStoresLoaded: PropTypes.func,
  onAddressSelect: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  delivery: PropTypes.bool
};

AddressAutocomplete.defaultProps = {
  onStoreSelect: () => {},
  onStoresLoaded: () => {},
  onAddressSelect: () => {},
  placeholder: 'Enter delivery address*',
  className: '',
  disabled: false,
  delivery: true,
};

export default AddressAutocomplete;
